import type { ReactNode } from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import SouthEastIcon from '@mui/icons-material/SouthEast';

export interface MetricCardProps {
  /** Optional leading glyph, shown in a soft brand-tinted square. */
  icon?: ReactNode;
  label: string;
  value: string;
  /** Period-over-period change, in %. Omit to hide the trend chip. */
  delta?: number;
  /**
   * Whether a *drop* is the good outcome (true for most support metrics like
   * frustration or rage-click rate). Controls the trend colour, not the arrow.
   */
  lowerIsBetter?: boolean;
  /** Icon tint; defaults to the brand accent. */
  accent?: string;
  onClick?: () => void;
}

/**
 * The single KPI tile used across the app: a brand-tinted icon, a big value,
 * a caption, and an optional coloured period-over-period delta. Extracted from
 * the Support Analytics page so every page shares one look.
 */
export function MetricCard({ icon, label, value, delta, lowerIsBetter = true, accent, onClick }: MetricCardProps) {
  const theme = useTheme();
  const tint = accent ?? theme.palette.primary.main;
  const up = (delta ?? 0) > 0;
  const good = up ? !lowerIsBetter : lowerIsBetter;
  const deltaColor = !delta
    ? theme.palette.text.secondary
    : good
      ? theme.palette.success.main
      : theme.palette.error.main;
  const hasTop = icon != null || delta != null;

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2.5,
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'box-shadow .15s ease, transform .15s ease',
        '&:hover': onClick ? { boxShadow: 3, transform: 'translateY(-1px)' } : undefined,
      }}
    >
      {hasTop && (
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ minHeight: 34 }}>
          {icon ? (
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 2,
                bgcolor: alpha(tint, 0.12),
                color: tint,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              {icon}
            </Box>
          ) : (
            <span />
          )}
          {delta != null && (
            <Stack direction="row" spacing={0.25} alignItems="center" sx={{ color: deltaColor }}>
              {up ? <NorthEastIcon sx={{ fontSize: 15 }} /> : <SouthEastIcon sx={{ fontSize: 15 }} />}
              <Typography variant="caption" fontWeight={700}>
                {up ? '+' : ''}
                {delta}%
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
      <Typography variant="h4" fontWeight={800} sx={{ mt: hasTop ? 1.5 : 0 }}>
        {value}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Paper>
  );
}
